import { postData } from './common/api';
import {
  fetchCart,
  getCartLevelDiscountAllocations,
  getCurrencyFromShopifyCart,
  getMRCartFromShopifyCart,
} from './common/cart';
import { getCartDiscountEndpoint } from './common/constants';
import { sendTotalPriceUpdateEvent } from './common/jsCustomEvents';
import { getCountryCode, getDevice, getPage, getShop } from './common/pageData';
import {
  getDiscountCodeFromApps,
  getOrderID,
  getOrderProperties,
  setMrDiscountCode,
  removeMrDiscountCode,
  getMrFreegiftDiscountCode,
  getIsShopifyFunctionsEnabled,
  getStorefrontAccessToken,
  getIsDiscountCodeRuleEnabled,
} from './common/settings';
import { sleep } from './common/time';
import { logger } from './common/logger';
import { getCustomerId } from './common/offerData';

export const checkAndUpdateCartDiscount = async ({
  isDiscountCodeEnabled,
  cartPageDiscountConfig,
  apiCart,
  discountCode,
}) => {
  const {
    cartDrawerTotalSelector,
    cartPageTotalSelector,
    cartPageProductPriceSelector,
    cartDrawerProductPriceSelector,
    discountCodeConfig,
    isEnabled,
  } = cartPageDiscountConfig;

  let discountCodeModule;
  if (isDiscountCodeEnabled) {
    discountCodeModule = await import('./features/discountCodeModule');
  }

  const getAppliedCodeFromResponse = discountCode =>
    discountCode.isValid && discountCode.code && discountCode.code !== ''
      ? discountCode.code
      : null;

  const hideEmbeddedDiscountCode = async () => {
    if (discountCodeModule) {
      await discountCodeModule.displayEmbeddedDiscountCode({
        show: false,
        cartPageConfig: {
          selector: discountCodeConfig.cartPageSelector,
          position: discountCodeConfig.cartPagePosition,
        },
        cartDrawerConfig: {
          selector: discountCodeConfig.cartDrawerSelector,
          position: discountCodeConfig.cartDrawerPosition,
        },
      });
    }
  };

  if (!isEnabled) {
    logger.log('cart discount display disabled');
    return null;
  }

  const request = await createCartDiscountRequest({
    apiCart,
    discountCode,
  });
  const { cart_level_discount_applications } = request;
  let resp = await postData(getCartDiscountEndpoint(), request);
  if (!resp) {
    return null;
  }
  if (getIsShopifyFunctionsEnabled()) {
    const start = performance.now();
    const sfModule = await import('./features/shopifyFunctionsDiscount');
    // will return modified response for monk codes, not for shopify codes
    resp = await sfModule.modifyCartDiscountResponse({
      resp,
      mrCart: request.cart,
      accessToken: getStorefrontAccessToken(),
      countryCode: getCountryCode(),
      mrOrderID: getOrderID(),
      shopURL: request.shop_url,
      cartToken: request.cart_token,
      cart_level_discount_applications,
      discountCode,
    });
    const end = performance.now();
    logger.log(end - start, 'time taken functions');
  }

  const {
    isZeroDiscount,
    discountedCart,
    discount_applicable,
    totalOfferPrice,
    totalOfferPriceFloat,
    totalPrice,
  } = resp;

  // always check for initialAppliedCode even if discount code widget is not displayed
  const initialAppliedCode = getAppliedCodeFromResponse(resp.discountCode); // returns code only if code is valid

  if (initialAppliedCode) {
    setMrDiscountCode(initialAppliedCode);
  } else {
    removeMrDiscountCode();
  }
  const isAutoAppliedFreegiftDiscountCode = !!getMrFreegiftDiscountCode();

  const showDiscountCodeWidget =
    resp.discount_applicable && // Product from monk widget are added
    !resp.isZeroDiscount && // Discount is applied on products added from monk widget
    resp.discountCode &&
    resp.discountCode.displayEmbedded;

  const showFreegiftDiscountCodeWidget =
    (resp.discountCode && resp.discountCode.isFreegiftDiscountCodeEnabled) ||
    getIsDiscountCodeRuleEnabled(); // Specific Freegift discount code is enabled by Monk app
  // check if discount is not applicable then revert price
  if (!discount_applicable || totalOfferPrice === totalPrice) {
    revertPrices();
    // if FDC is not enabled then remove discount widget
    if (!showFreegiftDiscountCodeWidget) {
      await hideEmbeddedDiscountCode();
      sendTotalPriceUpdateEvent({ price: 0, isMonkDiscountApplicable: false });
      return null;
    }
  }

  if (
    !isZeroDiscount &&
    totalOfferPrice &&
    totalPrice &&
    totalOfferPrice !== totalPrice
  ) {
    if (getShop() === 'ciberpanda.myshopify.com') {
      await sleep(2500);
    }

    if (getShop() === 'quirky-perks-new.myshopify.com') {
      await sleep(1500);
    }

    let totalPriceTimeout = 0;
    let productPriceTimeout = 0;
    let totalPriceTemplate = '{{price}} {{offerPrice}}';
    let productPriceTemplate = '{{price}} {{offerPrice}}';

    if (getShop() === 'golden-moneyfeaf.myshopify.com') {
      totalPriceTemplate = '{{offerPrice}} {{price}}';
      productPriceTemplate = '{{offerPrice}} {{price}}';
    }

    if (getShop() === '9b7cd0.myshopify.com') {
      totalPriceTemplate = '{{offerPrice}}';
    }

    if (getShop() === 'perspective-eu.myshopify.com') {
      totalPriceTimeout = 2000;
    }

    if (getShop() === 'revlon-co-in.myshopify.com') {
      totalPriceTimeout = 2000;
      productPriceTimeout = 2000;
    }

    setTimeout(() => {
      logger.log('updating prices cart page', cartPageProductPriceSelector);
      setProductPrices({
        selector: cartPageProductPriceSelector,
        discountedCart,
        template: productPriceTemplate,
      });
    }, productPriceTimeout);

    setTimeout(() => {
      setProductPrices({
        selector: cartDrawerProductPriceSelector,
        discountedCart,
        template: productPriceTemplate,
      });
    }, productPriceTimeout);

    setTimeout(() => {
      setTotalPrices({
        selector: cartDrawerTotalSelector,
        offerPrice: totalOfferPrice,
        price: totalPrice,
        template: totalPriceTemplate,
      });
    }, totalPriceTimeout);

    setTimeout(() => {
      setTotalPrices({
        selector: cartPageTotalSelector,
        offerPrice: totalOfferPrice,
        price: totalPrice,
        template: totalPriceTemplate,
      });
    }, totalPriceTimeout);

    if (getShop() === 'vilvahstore.myshopify.com') {
      // base price
      setTotalPrices({
        selector: '.cart-sub-total:nth-child(1) span.money',
        offerPrice: '',
        price: totalPrice,
        template: totalPriceTemplate,
      });

      // offer price
      setTotalPrices({
        selector: '.cart-sub-total:nth-child(2) span.money',
        offerPrice: totalOfferPrice,
        price: '',
        template: totalPriceTemplate,
      });
    }

    if (getShop() === 'muse-india.myshopify.com') {
      setTotalPrices({
        selector: '.cart-total__price',
        offerPrice: totalOfferPrice,
        price: totalPrice,
        template: totalPriceTemplate,
      });
    }

    if (getShop() === 'sidequest-apparel.myshopify.com') {
      setTotalPrices({
        selector: '#slideout-ajax-cart .subtotal-price',
        offerPrice: totalOfferPrice,
        price: totalPrice,
        template: totalPriceTemplate,
      });
      setTotalPrices({
        selector: '.main__content-wrapper .subtotal-price',
        offerPrice: totalOfferPrice,
        price: totalPrice,
        template: totalPriceTemplate,
      });
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('monk:discountApplicable', {
        bubbles: true,
        detail: {
          totalOfferPrice,
          totalPrice,
          discountedCart,
        },
        totalOfferPrice,
        totalPrice,
        discountedCart,
      }),
    );
    sendTotalPriceUpdateEvent({
      price: totalOfferPriceFloat,
      isMonkDiscountApplicable: true,
    });
  }

  if (showDiscountCodeWidget || showFreegiftDiscountCodeWidget) {
    discountCodeModule.displayEmbeddedDiscountCode({
      show: showDiscountCodeWidget
        ? resp.discountCode.displayEmbedded
        : showFreegiftDiscountCodeWidget,
      offerType: showFreegiftDiscountCodeWidget
        ? resp.discountCode.discountCodeOfferType
        : null,
      theme: resp.discountCode.theme,
      cartPageConfig: {
        selector: discountCodeConfig.cartPageSelector,
        position: discountCodeConfig.cartPagePosition,
      },
      cartDrawerConfig: {
        selector: discountCodeConfig.cartDrawerSelector,
        position: discountCodeConfig.cartDrawerPosition,
      },
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      discountCodeValues: {
        initialAppliedCode,
        initialIsDisplayAppliedInfo:
          resp.discountCode && resp.discountCode.isDisplayAppliedInfo,
        isAutoAppliedFreegiftDiscountCode,
      },
      retryContainerCreation: true,
      mrCart: request.cart,
    });
  } else {
    await hideEmbeddedDiscountCode();
  }

  return resp;
};

const setTotalPrices = ({ selector, offerPrice, price, template }) => {
  if (!selector || selector === '') {
    return;
  }

  let priceToUse = price;
  if (getShop() === 'www-lussocloud-com.myshopify.com') {
    priceToUse = '';
  }

  let priceColor = 'inherit';
  let offerPriceColor = 'inherit';
  let offerPriceFontWeight = 'inherit';

  if (getShop() === 'maverick-concept.myshopify.com') {
    priceColor = '#ff0000';
    offerPriceColor = 'inherit';
    offerPriceFontWeight = 'bold';
  }

  const selectors = selector.split(',');
  const selectedNodes = [];
  for (let i = 0; i < selectors.length; i += 1) {
    if (selectors[i] === '') {
      // eslint-disable-next-line no-continue
      continue;
    }

    const nodes = document.querySelectorAll(selectors[i]);

    for (let j = 0; j < nodes.length; j += 1) {
      if (nodes[j] && nodes[j].offsetHeight > 0) {
        selectedNodes.push(nodes[j]);
      }
    }

    if (selectedNodes.length > 0) {
      break;
    }
  }

  if (selectedNodes.length < 1) {
    return;
  }

  for (let i = 0; i < selectedNodes.length; i += 1) {
    selectedNodes[i].innerHTML = getTotalPriceHTML({
      price: priceToUse,
      offerPrice,
      priceColor,
      offerPriceColor,
      offerPriceFontWeight,
      template,
    });
  }
};

const setProductPrices = ({ selector, discountedCart, template }) => {
  if (!selector || selector === '') {
    return;
  }

  const selectors = selector.split(',');
  let selectedNodes;
  for (let i = 0; i < selectors.length; i += 1) {
    if (selectors[i] === '') {
      // eslint-disable-next-line no-continue
      continue;
    }

    const nodes = document.querySelectorAll(selectors[i]);
    // if (nodes.length !== discountedCart.length) {
    //   continue;
    // }

    const nodesVisible = [];
    for (let j = 0; j < nodes.length; j += 1) {
      if (nodes[j].offsetHeight === 0) {
        // eslint-disable-next-line no-continue
        continue;
      }
      nodesVisible.push(nodes[j]);
    }

    if (nodesVisible.length === discountedCart.length) {
      selectedNodes = nodesVisible;
      break;
    }
    logger.Debug('number of items differ in cart and display');
  }

  if (!selectedNodes) {
    return;
  }

  for (let i = 0; i < selectedNodes.length; i += 1) {
    const selectedNode = selectedNodes[i];
    const product = discountedCart[i];
    const { totalPrice: price, totalOfferPrice: offerPrice } = product;
    if (price === offerPrice) {
      // eslint-disable-next-line no-continue
      continue;
    }
    let priceToUse = price;
    if (getShop() === 'www-lussocloud-com.myshopify.com') {
      priceToUse = '';
    }

    selectedNode.innerHTML = getProductPriceHTML({
      price: priceToUse,
      offerPrice,
      template,
    });
  }
};

const getProductPriceHTML = ({ price, offerPrice, template }) => {
  const templateToUse = template || '{{price}} {{offerPrice}}';
  return templateToUse
    .replace(
      '{{price}}',
      `<span class="mr-cd-line-price" style="text-decoration: line-through;">${price}</span>`,
    )
    .replace(
      '{{offerPrice}}',
      `<span class="mr-cd-line-offer-price" style="text-decoration: none;">${offerPrice}</span>`,
    );
};

const getTotalPriceHTML = ({
  price,
  offerPrice,
  priceColor,
  offerPriceColor,
  offerPriceFontWeight,
  template,
}) => {
  const templateToUse = template || '{{price}} {{offerPrice}}';

  return templateToUse
    .replace(
      '{{price}}',
      `<span class="mr-cd-total-price" style="text-decoration: line-through; color:${priceColor};">${price}</span>`,
    )
    .replace(
      '{{offerPrice}}',
      `<span class="mr-cd-total-offer-price" style="color:${offerPriceColor};font-weight:${offerPriceFontWeight};">${offerPrice}</span>`,
    );
};

const createCartDiscountRequest = async ({ apiCart }) => {
  const request = {};
  const cart = apiCart || (await fetchCart());
  const { token } = cart;
  request.currency = getCurrencyFromShopifyCart(cart);
  request.cart = getMRCartFromShopifyCart(cart);
  request.cart_level_discount_applications = getCartLevelDiscountAllocations(
    cart,
  );
  request.device = getDevice();
  request.mr_order_id = getOrderID();
  request.shop_url = getShop();
  request.page = getPage();
  request.properties = getOrderProperties();
  request.cart_token = token;
  request.customerId = getCustomerId();

  // if cart has hidden attributes, then set it
  if (cart.attributes) {
    Object.keys(cart.attributes).forEach(key => {
      request.properties[key] = cart.attributes[key];
    });
  }

  const discountCode = getDiscountCodeFromApps('apply');
  if (discountCode) {
    request.discountCode = discountCode;
  }

  return request;
};

const revertPrices = () => {
  revertPrice({
    priceSelector: '.mr-cd-line-price',
    offerPriceSelector: '.mr-cd-line-offer-price',
  });
  revertPrice({
    priceSelector: '.mr-cd-total-price',
    offerPriceSelector: '.mr-cd-total-offer-price',
  });
};

const revertPrice = ({ priceSelector, offerPriceSelector }) => {
  const priceElements = document.querySelectorAll(priceSelector);
  for (let i = 0; i < priceElements.length; i += 1) {
    priceElements[i].style.textDecoration = 'none';
  }

  const offerPriceElements = document.querySelectorAll(offerPriceSelector);
  for (let i = 0; i < offerPriceElements.length; i += 1) {
    offerPriceElements[i].style.display = 'none';
  }
};

// export const init = ({ isDiscountCodeEnabled, cartPageDiscountConfig }) => {
//   document.documentElement.addEventListener('mr:cartDiscount', () => {
//     checkAndUpdateCartDiscount({
//       isDiscountCodeEnabled,
//       cartPageDiscountConfig,
//     });
//   });
// };
